import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

function ChartCardSkeleton({ barCount = 10 }: { barCount?: number }) {
  const heights = [40, 65, 50, 80, 70, 55, 90, 45, 75, 60].slice(0, barCount);
  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
    >
      <CardHeader>
        <Skeleton className="h-4 w-36" />
      </CardHeader>
      <CardContent>
        {/* Chart area with bars */}
        <div className="flex h-56 items-end gap-2 pt-4">
          {heights.map((h, i) => (
            <Skeleton key={i} className="flex-1 rounded-t-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
        {/* X-axis labels */}
        <div className="mt-2 flex gap-2">
          {heights.map((_, i) => (
            <Skeleton key={i} className="h-2 flex-1" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCardSkeleton() {
  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
    >
      <CardHeader>
        <Skeleton className="h-4 w-32" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex items-baseline justify-between py-1">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3.5 w-14" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TableCardSkeleton() {
  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
    >
      <CardHeader>
        <Skeleton className="h-4 w-28" />
      </CardHeader>
      <CardContent className="p-0">
        {/* Table header */}
        <div className="flex items-center gap-4 border-b px-4 py-2">
          {[120, 64, 72, 48, 80].map((w, i) => (
            <Skeleton key={i} className="h-3" style={{ width: w }} />
          ))}
        </div>
        {/* Table rows */}
        {Array.from({ length: 5 }).map((_, row) => (
          <div key={row} className="flex items-center gap-4 border-b px-4 py-2.5 last:border-0">
            {[120, 64, 72, 48, 80].map((w, col) => (
              <Skeleton key={col} className="h-3" style={{ width: w + (row % 2) * 8 }} />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DonutCardSkeleton() {
  return (
    <Card
      className="bg-gradient-card"
      style={{ boxShadow: 'var(--shadow-2), var(--inset-highlight)' }}
    >
      <CardHeader>
        <Skeleton className="h-4 w-36" />
      </CardHeader>
      <CardContent className="flex items-center justify-center">
        <Skeleton className="h-48 w-48 rounded-full" />
      </CardContent>
    </Card>
  );
}

export function StatsSkeleton() {
  return (
    <div className="space-y-4">
      {/* Top row: Score Distribution chart (1fr) + Summary Stats card (320px) */}
      <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
        <ChartCardSkeleton />
        <SummaryCardSkeleton />
      </div>

      {/* Bottom row: two charts side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCardSkeleton barCount={6} />
        <ChartCardSkeleton barCount={4} />
      </div>

      {/* Top Advisors table */}
      <TableCardSkeleton />
    </div>
  );
}

export function ForwardsTabSkeleton() {
  return (
    <div className="space-y-4 pt-2">
      {/* Top row: chart + summary card */}
      <div className="grid gap-4 lg:grid-cols-[1fr,320px]">
        <ChartCardSkeleton barCount={6} />
        <SummaryCardSkeleton />
      </div>

      {/* Bottom row: chart + donut */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCardSkeleton barCount={6} />
        <DonutCardSkeleton />
      </div>
    </div>
  );
}
